/**
 * Packaging OS — Brands catalog (Phase 1O-B).
 *
 * Read-only paginated list of GET /api/brands. All non-success branches
 * delegated to <DataState />. No editing — Phase 2.
 */

import { useEffect, useState } from "react";
import {
  getBrands,
  type ApiResult,
  type BrandRow,
  type PaginatedList,
} from "../api/client";
import { DataState, RequestIdBadge } from "../components/DataState";
import { DataTable, type DataTableColumn } from "../components/DataTable";
import { strings } from "../i18n/strings";
import { useLocale } from "../i18n/useLocale";
import { AppLayout } from "./CatalogLayout";
import { CatalogPagination } from "./catalogShared";
import { formatDate, useCatalogPage } from "./catalogHelpers";

type LoadState =
  | { kind: "loading" }
  | { kind: "loaded"; result: ApiResult<PaginatedList<BrandRow>> };

const PAGE_SIZE = 25;

export function BrandsPage(): JSX.Element {
  const [locale] = useLocale();
  const t = strings(locale);
  const { page, setPage } = useCatalogPage();
  const [state, setState] = useState<LoadState>({ kind: "loading" });

  useEffect(() => {
    let cancelled = false;
    setState({ kind: "loading" });
    void (async () => {
      const result = await getBrands({ page, pageSize: PAGE_SIZE });
      if (!cancelled) setState({ kind: "loaded", result });
    })();
    return () => {
      cancelled = true;
    };
  }, [page]);

  const columns: DataTableColumn<BrandRow>[] = [
    {
      key: "name",
      header: t.catalogColumnName,
      render: (r) => r.name,
    },
    {
      key: "slug",
      header: t.catalogColumnSlug,
      render: (r) => <span className="po-table__cell-id dir-ltr-island">{r.slug}</span>,
    },
    {
      key: "version",
      header: t.catalogColumnVersion,
      render: (r) => (
        <span className="po-table__cell-id">v{r.version}</span>
      ),
    },
    {
      key: "updated",
      header: t.catalogColumnUpdated,
      render: (r) => <span className="dir-ltr-island">{formatDate(r.updated_at)}</span>,
    },
  ];

  return (
    <AppLayout>
      <header className="po-section-header">
        <span className="po-section-header__eyebrow">{t.appShellNavCatalog}</span>
        <h1 className="po-section-header__title">{t.catalogBrandsTitle}</h1>
        <p className="po-note po-note--muted">{t.catalogReadOnlyNotice}</p>
      </header>

      <DataState
        state={state}
        t={t}
        emptyWhen={(d) => d.items.length === 0}
        emptyTitle={t.catalogEmptyTitle}
        emptyBody={t.catalogEmptyBody}
        renderOk={(data, requestId) => (
          <div className="po-stack">
            <DataTable
              rows={data.items}
              columns={columns}
              rowKey={(r) => r.brand_id}
              caption={t.catalogBrandsTitle}
            />
            <CatalogPagination
              page={data.page}
              pageSize={data.page_size}
              total={data.total}
              hasNext={data.has_next}
              onChange={setPage}
              t={t}
            />
            {requestId && <RequestIdBadge t={t} requestId={requestId} />}
          </div>
        )}
      />
    </AppLayout>
  );
}
